import { useCallback, useEffect, useReducer } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  allUnselectedState,
  getShowFiltersParams,
  getShowFiltersState,
  type useShowFiltersParams,
} from './';

export interface ShowFilterItems<T> {
  label: string;
  selected: boolean;
  value: T;
  count?: number;
}

export interface ShowFilter<T> {
  items: ShowFilterItems<T>[];
  open: boolean;
}

export interface ShowFilters {
  tags: ShowFilter<string>;
}

export interface ShowPanel {
  open: boolean;
}

export interface ShowFiltersState {
  filters: ShowFilters;
  panel: ShowPanel;
}

export type ShowFiltersAction =
  | { type: 'CLEAR_FILTERS' }
  | { type: 'REINITIALIZE'; params: string }
  | ({ type: 'SET_FILTER'; keepPage?: boolean; open?: boolean } & {
      [K in keyof ShowFilters]: { field: K; items: ShowFilters[K]['items'] };
    }[keyof ShowFilters])
  | { type: 'TOGGLE_FILTER'; field: keyof ShowFilters }
  | { type: 'TOGGLE_PANEL' };

export const useShowFiltersReducer = (
  params: ReturnType<typeof useShowFiltersParams>,
  showTags: readonly string[] | undefined,
) => {
  const [_, setSearchParams] = useSearchParams();

  const reducer = useCallback(
    (state: ShowFiltersState, action: ShowFiltersAction): ShowFiltersState => {
      switch (action.type) {
        case 'CLEAR_FILTERS': {
          setSearchParams((params) => {
            Object.keys(state.filters).forEach((x) => params.delete(x));
            params.set('page', '1');
            return params;
          });

          return allUnselectedState;
        }

        case 'REINITIALIZE': {
          const urlSearchParams = new URLSearchParams(action.params);
          const params = getShowFiltersParams(urlSearchParams);
          setSearchParams(() => urlSearchParams);
          return getShowFiltersState(params);
        }

        case 'SET_FILTER': {
          const { field, items }: { field: keyof ShowFilters; items: ShowFilterItems<string>[] } =
            action;
          const { keepPage = false } = action;
          const selected = items.filter((x) => x.selected);
          setSearchParams((params) => {
            selected.length > 0
              ? params.set(field, JSON.stringify(selected.map((x) => x.value)))
              : params.delete(field);
            if (!keepPage) params.set('page', '1'); // reset pagination when filters change
            return params;
          });
          return {
            ...state,
            filters: {
              ...state.filters,
              [field]: {
                ...state.filters[field],
                ...(action.open != null && { open: action.open }),
                items,
              },
            },
          };
        }

        case 'TOGGLE_FILTER': {
          return {
            ...state,
            filters: {
              ...state.filters,
              [action.field]: {
                ...state.filters[action.field],
                open: !state.filters[action.field].open,
              },
            },
          };
        }

        case 'TOGGLE_PANEL': {
          return {
            ...state,
            panel: { ...state.panel, open: !state.panel.open },
          };
        }

        default: {
          return state;
        }
      }
    },
    [setSearchParams],
  );

  const [state, dispatch] = useReducer(reducer, getShowFiltersState(params));

  // Update tags when showTags from GraphQL changes
  useEffect(() => {
    if (!showTags) return;
    if (showTags.join() === state.filters.tags.items.map((x) => x.value).join()) return;
    dispatch({
      type: 'SET_FILTER',
      field: 'tags',
      items: showTags.map((x) => ({
        label: x,
        selected: params.tags.includes(x),
        value: x,
      })),
      keepPage: true,
      open: params.tags.length > 0,
    });
  }, [showTags, params.tags, state.filters.tags.items]);

  return [state, dispatch] as const;
};
